
import { Component, Prop, Watch } from 'vue-property-decorator';
import Page from '@/Page.vue';
import {
    GanttTimelineData,
    WorkplaceRow,
    TimeSegment,
    GanttEvent,
    TooltipState,
} from '@/libs/tpm-analytics/core/gantt-timeline.types';
import GanttTooltip from './GanttTooltip.vue';

interface TimeAxisTick {
    x: number;
    label: string;
    isMajor: boolean;
}

interface VisibleWorkplaceRow extends WorkplaceRow {
    offsetY: number;
}

@Component({
    components: {
        GanttTooltip,
    },
})
export default class GanttChart extends Page {
    @Prop({ required: true }) data!: GanttTimelineData;
    @Prop({ default: 'hourly' }) granularity!: 'hourly' | 'daily' | 'weekly';
    @Prop({ default: 30 }) pixelsPerHour!: number;
    @Prop({ default: 40 }) rowHeight!: number;

    // Scroll state
    scrollTop = 0;
    scrollLeft = 0;

    // Viewport dimensions
    viewportHeight = 600;
    viewportWidth = 1200;

    // Konstanty
    segmentMargin = 4;
    eventMarkerRadius = 5;

    // Tooltip state
    tooltipState: TooltipState = {
        visible: false,
        x: 0,
        y: 0,
        event: null,
    };

    // Scroll sync flag
    isSyncingScroll = false;

    mounted() {
        this.updateViewportDimensions();
        window.addEventListener('resize', this.updateViewportDimensions);
    }

    destroyed() {
        window.removeEventListener('resize', this.updateViewportDimensions);
    }

    get totalHeight(): number {
        return this.data.workplaces.length * this.rowHeight;
    }

    get totalWidth(): number {
        const rangeMs = this.data.timeRange.to.getTime() - this.data.timeRange.from.getTime();
        const hours = rangeMs / (1000 * 60 * 60);
        return hours * this.pixelsPerHour;
    }

    get visibleWorkplaces(): VisibleWorkplaceRow[] {
        const startIdx = Math.max(0, Math.floor(this.scrollTop / this.rowHeight) - 2);
        const endIdx = Math.min(
            this.data.workplaces.length,
            Math.ceil((this.scrollTop + this.viewportHeight) / this.rowHeight) + 2
        );

        const visible: VisibleWorkplaceRow[] = [];
        for (let i = startIdx; i < endIdx; i++) {
            const row = this.data.workplaces[i];
            visible.push({
                ...row,
                offsetY: i * this.rowHeight,
            });
        }

        return visible;
    }

    get timeAxisTicks(): TimeAxisTick[] {
        const ticks: TimeAxisTick[] = [];
        const from = this.data.timeRange.from.getTime();
        const to = this.data.timeRange.to.getTime();

        if (this.granularity === 'hourly') {
            let current = new Date(from);
            current.setMinutes(0, 0, 0);

            while (current.getTime() <= to) {
                const x = this.timeToX(current);
                const isMajor = current.getHours() === 0;
                const label = isMajor
                    ? current.toLocaleDateString('cs-CZ', { day: '2-digit', month: '2-digit' })
                    : current.getHours().toString().padStart(2, '0') + ':00';

                ticks.push({ x, label, isMajor });
                current = new Date(current.getTime() + 60 * 60 * 1000);
            }
        } else if (this.granularity === 'daily') {
            let current = new Date(from);
            current.setHours(0, 0, 0, 0);

            while (current.getTime() <= to) {
                const x = this.timeToX(current);
                const isMajor = current.getDate() === 1;
                const label = isMajor
                    ? current.toLocaleDateString('cs-CZ', { month: 'short', year: 'numeric' })
                    : current.getDate().toString();

                ticks.push({ x, label, isMajor });
                current = new Date(current.getTime() + 24 * 60 * 60 * 1000);
            }
        }

        return ticks;
    }

    timeToX(time: Date): number {
        const fromMs = this.data.timeRange.from.getTime();
        const deltaMs = time.getTime() - fromMs;
        const hours = deltaMs / (1000 * 60 * 60);
        return hours * this.pixelsPerHour;
    }

    getSegmentWidth(segment: TimeSegment): number {
        const from = segment.from.getTime();
        const to = segment.to ? segment.to.getTime() : new Date().getTime();
        const deltaMs = to - from;
        const hours = deltaMs / (1000 * 60 * 60);
        return Math.max(1, hours * this.pixelsPerHour);
    }

    onLabelsScroll() {
        if (this.isSyncingScroll) return;
        this.isSyncingScroll = true;

        const labelsContainer = this.$refs.labelsContainer as HTMLElement;
        const ganttContainer = this.$refs.ganttContainer as HTMLElement;
        
        if (ganttContainer) {
            ganttContainer.scrollTop = labelsContainer.scrollTop;
            this.scrollTop = labelsContainer.scrollTop;
        }

        this.$nextTick(() => {
            this.isSyncingScroll = false;
        });
    }

    onGanttScroll() {
        if (this.isSyncingScroll) return;
        this.isSyncingScroll = true;

        const labelsContainer = this.$refs.labelsContainer as HTMLElement;
        const ganttContainer = this.$refs.ganttContainer as HTMLElement;
        const timeAxisContainer = this.$refs.timeAxisContainer as HTMLElement;
        
        // Synchronizovat vertikální scroll s levým panelem
        if (labelsContainer) {
            labelsContainer.scrollTop = ganttContainer.scrollTop;
        }
        
        // Synchronizovat horizontální scroll s časovou osou
        if (timeAxisContainer) {
            timeAxisContainer.scrollLeft = ganttContainer.scrollLeft;
        }
        
        this.scrollTop = ganttContainer.scrollTop;
        this.scrollLeft = ganttContainer.scrollLeft;

        this.$nextTick(() => {
            this.isSyncingScroll = false;
        });
    }

    onTimeAxisScroll() {
        if (this.isSyncingScroll) return;
        this.isSyncingScroll = true;

        const timeAxisContainer = this.$refs.timeAxisContainer as HTMLElement;
        const ganttContainer = this.$refs.ganttContainer as HTMLElement;
        
        if (ganttContainer) {
            ganttContainer.scrollLeft = timeAxisContainer.scrollLeft;
            this.scrollLeft = timeAxisContainer.scrollLeft;
        }

        this.$nextTick(() => {
            this.isSyncingScroll = false;
        });
    }

    updateViewportDimensions() {
        const container = this.$refs.container as HTMLElement;
        if (!container) return;

        this.viewportWidth = container.clientWidth;
        this.viewportHeight = Math.min(container.clientHeight - 100, 800);
    }

    showEventTooltip(event: GanttEvent, mouseEvent: MouseEvent) {
        const rect = (this.$refs.container as HTMLElement).getBoundingClientRect();
        this.tooltipState = {
            visible: true,
            x: mouseEvent.clientX - rect.left,
            y: mouseEvent.clientY - rect.top,
            event,
        };
    }

    hideEventTooltip() {
        this.tooltipState.visible = false;
    }

    @Watch('data')
    onDataChanged() {
        this.scrollTop = 0;
        this.scrollLeft = 0;
    }
}
